/**
 * ============================================================================
 * Upload Progress Component
 * ============================================================================
 * Displays real-time upload progress with animated progress bar
 * ============================================================================
 */

import { Progress } from "@/components/ui/progress";
import { CheckCircle2, Loader2, Upload } from "lucide-react";

export interface UploadProgressProps {
  fileName: string;
  status: "parsing" | "uploading" | "complete" | "error";
  progress: number; // 0-100
  currentRows?: number;
  totalRows?: number;
  error?: string;
}

export default function UploadProgress({
  fileName,
  status,
  progress,
  currentRows,
  totalRows,
  error,
}: UploadProgressProps) {
  const getStatusIcon = () => {
    switch (status) {
      case "parsing":
        return <Loader2 className="h-5 w-5 animate-spin text-blue-500" />;
      case "uploading":
        return <Upload className="h-5 w-5 animate-pulse text-emerald-500" />;
      case "complete":
        return <CheckCircle2 className="h-5 w-5 text-green-500" />;
      case "error":
        return <div className="h-5 w-5 rounded-full bg-red-500 flex items-center justify-center text-white text-xs">!</div>;
    }
  };

  const getStatusText = () => {
    switch (status) {
      case "parsing":
        return "Parsing Excel file...";
      case "uploading":
        return currentRows && totalRows
          ? `Uploading: ${currentRows.toLocaleString()} / ${totalRows.toLocaleString()} rows`
          : "Uploading data...";
      case "complete":
        return `✅ Upload complete! ${totalRows?.toLocaleString() || 0} rows inserted`;
      case "error":
        return `❌ Upload failed: ${error || "Unknown error"}`;
    }
  };

  const getProgressColor = () => {
    switch (status) {
      case "parsing":
        return "bg-blue-500";
      case "uploading":
        return "bg-emerald-500";
      case "complete":
        return "bg-green-500";
      case "error":
        return "bg-red-500";
    }
  };

  return (
    <div className="space-y-3 p-4 border rounded-lg bg-card">
      {/* Header */}
      <div className="flex items-center gap-3">
        {getStatusIcon()}
        <div className="flex-1">
          <p className="font-medium text-sm">{fileName}</p>
          <p className="text-xs text-muted-foreground">{getStatusText()}</p>
        </div>
        <div className="text-right">
          <p className="text-lg font-bold text-primary">{Math.round(progress)}%</p>
        </div>
      </div>

      {/* Progress Bar */}
      <div className="relative">
        <Progress value={progress} className="h-2" />
        <div
          className={`absolute top-0 left-0 h-2 rounded-full transition-all duration-300 ${getProgressColor()}`}
          style={{ width: `${progress}%` }}
        />
      </div>

      {/* Detailed Info */}
      {status === "uploading" && currentRows && totalRows && (
        <div className="flex justify-between text-xs text-muted-foreground">
          <span>Batch progress</span>
          <span>{Math.round((currentRows / totalRows) * 100)}% complete</span>
        </div>
      )}
    </div>
  );
}

